import {
  BarChart3,
  CalendarCheck,
  ClipboardCheck,
  Code,
  CreditCard,
  House,
  LayoutGrid,
  Layers,
  Network,
  type LucideIcon,
  Mail,
  MessageSquare,
  PieChart,
  Search,
  Settings,
  HelpCircle,
  Star,
  Users,
  FileText,
} from 'lucide-react';

import { ROUTES } from '@/config/routes';

/**
 * A screen this application routes to, and what it takes to reach it.
 *
 * <h2>Why this exists apart from the menu</h2>
 *
 * Until 8 October 2026 the menu **was** the route table. `placeholderRoutes` generated one
 * route per `NAV_ITEMS` entry and `navItemForPath` resolved a screen's products by searching
 * the same array — so the sidebar list decided three separate things at once: what is drawn,
 * what is routable, and what is guarded.
 *
 * <p>That is fine until a row has to be hidden. Taking Lead Meeting Intelligence out of the
 * rail would have **deleted `/lead-meeting-intelligence` from the router** and removed the
 * product check in front of it; moving My Account into the profile menu would have done the
 * same to `/account`. Neither is a menu change — both silently orphan a working module.
 *
 * <p>So the two concerns are separated. This array is the registry: every screen, its route,
 * and the products or roles that gate it. {@link ./navItems} is a **view** over it — ordered,
 * labelled, nested, and free to omit a screen without the screen ceasing to exist.
 *
 * <h2>What reads this, and what each one takes</h2>
 *
 * | Reader | Uses |
 * |---|---|
 * | `features/placeholder` | every screen with no real page yet, to generate its route |
 * | `productAccess.screenForPath` | the products behind a path, for `ProductRoute` |
 * | `navItems` | route, products and roles for the rows it draws |
 *
 * <p><b>A screen listed here with no menu row is still routable and still guarded.</b> That is
 * the whole point of the split, and the three rows the sidebar currently omits —
 * Lead Meeting Intelligence, My Account and the two agents — are the cases it was made for.
 */
/** Who may open Review Management. `super-admin` today; the platform admin role joins it later. */
export const REVIEW_MANAGEMENT_ROLES = ['super-admin'] as const;

export interface Screen {
  /** Stable id. The menu refers to a screen by this. */
  id: string;
  /**
   * What a placeholder page calls itself.
   *
   * <p>Not the menu label. The two were one string while the menu generated the routes, and
   * they are different things: the rail says "Home" where the screen is "Dashboard", and a
   * screen with no row has no menu label at all.
   */
  title: string;
  /** The quieter second line, on the placeholder and on the row that shows one. */
  description: string | null;
  route: string;
  /**
   * The screen's icon.
   *
   * <p>On the screen rather than on the menu row because **two things draw it**: the rail,
   * and `ProductNotAssigned` when it refuses the screen. That refusal shows the product's own
   * icon rather than a padlock — a padlock is the same picture on all six and says only "no" —
   * so a screen with no menu row still needs one.
   */
  icon: LucideIcon;
  /**
   * The products that open this screen, if any. **Any-of**, not all-of — see
   * {@link ./productAccess}.
   *
   * <p>Absent means no product gate. That is not the same as unguarded: Team and the
   * operator's own screens are gated by role instead, and every endpoint behind any of
   * these refuses on its own regardless.
   */
  productKeys?: readonly string[];
  /**
   * The roles that may see the menu row for this screen, where it is restricted to some.
   *
   * <p>Read by the sidebar, not by `ProductRoute` — a role gate on a screen is enforced by
   * its own guard (`SuperAdminRoute`) and by the server. Kept here so the row and the screen
   * cannot disagree about who the screen is for.
   */
  roleKeys?: readonly string[];
}

/**
 * Every screen behind a route this application owns.
 *
 * <p>Order is not significant — the menu decides what it shows and in what sequence. Adding a
 * screen here makes it routable and guarded; adding a row in {@link ./navItems} makes it
 * visible. The two are deliberately separate edits.
 */
export const SCREENS: readonly Screen[] = [
  {
    id: 'dashboard',
    icon: House,
    title: 'Dashboard',
    description: null,
    route: ROUTES.dashboard,
  },
  {
    id: 'ai-agents',
    icon: LayoutGrid,
    title: 'AI Agents',
    description: 'AI agent setup and analysis',
    route: ROUTES.aiAgent,
    productKeys: ['seo_ai_agent', 'linkedin_ai_agent'],
  },
  {
    /*
     * Registered 8 October 2026 so the AI Agents submenu has somewhere to point. The path is
     * the previous system's -- `AppNav.tsx` sends "SEO Agent" to `/agents/seo` -- so a deep
     * link that worked there works here.
     *
     * The page is NOT ported: this resolves to the work-in-progress screen like every other
     * unbuilt product screen. One product rather than two, so that screen can ask about
     * `seo_ai_agent` specifically, which the parent row cannot.
     */
    id: 'seo-agent',
    icon: Search,
    title: 'SEO Agent',
    description: 'Content & search growth',
    route: ROUTES.seoAgent,
    productKeys: ['seo_ai_agent'],
  },
  {
    /* Step 1 of the SEO setup. Registered for the guard, not for a menu row. */
    id: 'seo-agent-welcome',
    icon: Search,
    title: 'SEO Agent setup',
    description: 'Tell the agent about your business',
    route: ROUTES.seoAgentWelcome,
    productKeys: ['seo_ai_agent'],
  },
  {
    /* Steps 2 to 6 of the SEO setup, chosen by `?step=`. */
    id: 'seo-agent-setup',
    icon: Search,
    title: 'SEO Agent setup',
    description: 'Set up your SEO agent',
    route: ROUTES.seoAgentSetup,
    productKeys: ['seo_ai_agent'],
  },
  {
    /* One Create page or post request. Two segments deep, so the tab pattern below does not cover it. */
    id: 'seo-agent-custom-content',
    icon: Search,
    title: 'SEO Agent request',
    description: 'One page or post the agent was asked to create',
    route: ROUTES.seoAgentCustomContent,
    productKeys: ['seo_ai_agent'],
  },
  {
    /* The agent's settings. Ahead of the tab pattern below, which `/agents/seo/settings` also fits. */
    id: 'seo-agent-settings',
    icon: Search,
    title: 'SEO Agent settings',
    description: "Your SEO agent's configuration and connections",
    route: ROUTES.seoAgentSettings,
    productKeys: ['seo_ai_agent'],
  },
  {
    /* Every SEO dashboard tab past the Overview. After the setup entries so those match first. */
    id: 'seo-agent-tab',
    icon: Search,
    title: 'SEO Agent',
    description: 'Content & search growth',
    route: ROUTES.seoAgentTab,
    productKeys: ['seo_ai_agent'],
  },
  {
    /* One task of the plan. Two segments deep, so the tab pattern above does not cover it. */
    id: 'seo-agent-task',
    icon: Search,
    title: 'SEO Agent task',
    description: 'One task of your SEO plan',
    route: ROUTES.seoAgentTask,
    productKeys: ['seo_ai_agent'],
  },
  {
    /* What a task changed on one page. */
    id: 'seo-agent-task-changes',
    icon: Search,
    title: 'SEO Agent page changes',
    description: 'What the agent changed on a page',
    route: ROUTES.seoAgentTaskChanges,
    productKeys: ['seo_ai_agent'],
  },
  {
    /* The overview. Its path is the previous system's own, so a deep link that worked there works here. */
    id: 'linkedin-agent',
    icon: BarChart3,
    title: 'LinkedIn Agent',
    description: 'Outreach & engagement',
    route: ROUTES.linkedinAgent,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-settings',
    icon: Network,
    title: 'LinkedIn agent settings',
    description: 'Edit your configuration, AI team and reference profiles',
    route: ROUTES.linkedinSettings,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-welcome',
    icon: Network,
    title: 'LinkedIn Agent setup',
    description: 'Tell your agent about your business',
    route: ROUTES.linkedinWelcome,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-company-details',
    icon: Network,
    title: 'LinkedIn company details',
    description: 'Tell your agent about your business',
    route: ROUTES.linkedinCompanyDetails,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-configuration',
    icon: Network,
    title: 'LinkedIn Agent configuration',
    description: 'Identity, customers, story and goals',
    route: ROUTES.linkedinConfiguration,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-launch',
    icon: Network,
    title: 'Launch LinkedIn Agent',
    description: 'Build your team and activate your agent',
    route: ROUTES.linkedinLaunch,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-references',
    icon: Network,
    title: 'LinkedIn reference profiles',
    description: 'Models for who the agent should reach',
    route: ROUTES.linkedinReferences,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-prospects',
    icon: Network,
    title: 'LinkedIn prospects',
    description: 'Review fit, approve prospects and follow outreach',
    route: ROUTES.linkedinProspects,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    /* One prospect: who they are, why they scored as they did, and how far they have got. */
    id: 'linkedin-prospect-detail',
    icon: Network,
    title: 'LinkedIn prospect',
    description: 'One prospect and their progress',
    route: ROUTES.linkedinProspectDetail,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-strategy',
    icon: Network,
    title: 'LinkedIn strategy',
    description: 'The plan, day by day',
    route: ROUTES.linkedinStrategy,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-conversations',
    icon: Network,
    title: 'LinkedIn conversations',
    description: 'Review messages and comments before the agent responds',
    route: ROUTES.linkedinConversations,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-content',
    icon: Network,
    title: 'LinkedIn content',
    description: 'Draft, review and publish LinkedIn posts',
    route: ROUTES.linkedinContent,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-approvals',
    icon: Network,
    title: 'LinkedIn approvals',
    description: 'Everything the agent is waiting for you to approve',
    route: ROUTES.linkedinApprovals,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-activity',
    icon: Network,
    title: 'LinkedIn activity',
    description: 'Everything the agent has done, as it happens',
    route: ROUTES.linkedinActivity,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-analytics',
    icon: BarChart3,
    title: 'LinkedIn analytics',
    description: 'Outreach, engagement and content results',
    route: ROUTES.linkedinAnalytics,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'linkedin-review',
    icon: Network,
    title: 'Review LinkedIn setup',
    description: 'Check everything before the agent starts',
    route: ROUTES.linkedinReview,
    productKeys: ['linkedin_ai_agent'],
  },
  {
    id: 'brand-analyser',
    icon: PieChart,
    title: 'Brand Analyzer',
    description: 'Asset management',
    route: ROUTES.brandAnalyser,
    productKeys: ['brand_intelligence'],
  },
  {
    id: 'reviews',
    icon: Star,
    title: 'Reviews & Ratings',
    description: 'Track insights & trends',
    route: ROUTES.reviews,
    productKeys: ['reviews_rankings'],
  },
  {
    /* The Reviews & Ratings sub-screens: registered so `ProductRoute` guards them, not for a menu row. */
    id: 'reviews-responses',
    icon: MessageSquare,
    title: 'Review Responses',
    description: 'Reply to reviews',
    route: ROUTES.reviewsResponses,
    productKeys: ['reviews_rankings'],
  },
  {
    id: 'reviews-aggregation',
    icon: Star,
    title: 'Review Aggregation',
    description: 'Reviews from every platform',
    route: ROUTES.reviewsAggregation,
    productKeys: ['reviews_rankings'],
  },
  {
    id: 'review-forms',
    icon: FileText,
    title: 'Review Forms',
    description: 'Collect reviews on your website',
    route: ROUTES.reviewForms,
    productKeys: ['reviews_rankings'],
  },
  {
    /*
     * NOT in the sidebar since 8 October 2026, and deliberately still here.
     *
     * The rail was redesigned to the shape the previous system's now has, which has no row
     * for this product. The screen stays registered, so `/lead-meeting-intelligence` still
     * routes and `ProductRoute` still demands `lead_meeting_intelligence` in front of it --
     * which is the reason this registry was split from the menu in the first place.
     *
     * Reachable by its URL and by nothing else today. Worth knowing rather than discovering:
     * somebody assigned this product has no way to find the screen from the interface.
     */
    id: 'lead-meeting-intelligence',
    icon: CalendarCheck,
    title: 'Lead Meeting Intelligence',
    description: 'Meeting insights & follow-ups',
    route: ROUTES.leadMeetingIntelligence,
    productKeys: ['lead_meeting_intelligence'],
  },
  {
    id: 'email-warmup',
    icon: Mail,
    title: 'Email Warmup',
    description: 'Email deliverability tools',
    route: ROUTES.emailWarmup,
    productKeys: ['email_warmup'],
  },
  {
    /*
     * The detail screen, registered so `ProductRoute` guards it -- NOT so it gets a menu row.
     * It is a pattern rather than a path, which `screenForPath` matches with `matchPath`;
     * before 8 October 2026 that function compared strings, so a parameterised route could
     * not be guarded at all and this one would have been the first to slip through.
     *
     * Same product as the list, because it is the same product: a mailbox somebody may not
     * list is a mailbox they may not open.
     */
    id: 'email-warmup-account',
    icon: Mail,
    title: 'Email Warmup account',
    description: 'One mailbox being warmed',
    route: ROUTES.emailWarmupAccount,
    productKeys: ['email_warmup'],
  },
  {
    /*
     * Connecting a mailbox. Registered for the guard, not for a menu row -- the design has
     * none, and the way in is the button on the list.
     *
     * The server demands `email_warmup` + `create` on the endpoint itself, so this only
     * decides what somebody is SHOWN. `ProductRoute` asks products and not privileges:
     * nothing in this application gates on a privilege yet (CLAUDE.md, navigation NV-13),
     * so a team member holding the product but not `create` reaches the form and is
     * refused on submit. Stated here because it is a gap, not a decision.
     */
    id: 'email-warmup-create',
    icon: Mail,
    title: 'Add email account',
    description: 'Connect a mailbox for warming',
    route: ROUTES.emailWarmupCreate,
    productKeys: ['email_warmup'],
  },
  {
    /*
     * The plans. Registered for the guard, not for a menu row: the way in is the "View plans" button on
     * the list. Same product as the list, because a company that may not use Email Warmup has no
     * business choosing its plan.
     */
    id: 'email-warmup-billing',
    icon: Mail,
    title: 'Email Warmup plans',
    description: 'Plans and requests for Email Warmup',
    route: ROUTES.emailWarmupBilling,
    productKeys: ['email_warmup'],
  },
  {
    /*
     * `brand_api`, and it said `api_access` until 30 September 2026 -- a key the catalogue
     * stopped selling on 17 September, so this joined against nothing and could never be
     * shown as available.
     */
    id: 'developer-tools',
    icon: Code,
    title: 'Developer Tools',
    description: 'API & integration tools',
    route: ROUTES.developerTools,
    productKeys: ['brand_api'],
  },
  /*
   * The other Developer Tools screens: registered so `ProductRoute` demands `brand_api` in front
   * of each, not for a menu row -- they are reached from the Developer Tools screen itself.
   */
  {
    id: 'developer-pricing',
    icon: Code,
    title: 'Developer Pricing',
    description: 'Plans and add-ons',
    route: ROUTES.developerPricing,
    productKeys: ['brand_api'],
  },
  {
    id: 'api-console',
    icon: Code,
    title: 'API Console',
    description: 'Try a request',
    route: ROUTES.apiConsole,
    productKeys: ['brand_api'],
  },
  {
    id: 'api-key-detail',
    icon: Code,
    title: 'API key',
    description: "One key's usage and settings",
    route: ROUTES.apiKeyDetail,
    productKeys: ['brand_api'],
  },
  {
    id: 'developer-api-documentation',
    icon: Code,
    title: 'API Documentation',
    description: 'How to call the API',
    route: ROUTES.developerApiDocumentation,
    productKeys: ['brand_api'],
  },
  {
    id: 'developer-mcp-documentation',
    icon: Code,
    title: 'MCP Documentation',
    description: 'How to use the MCP server',
    route: ROUTES.developerMcpDocumentation,
    productKeys: ['brand_api'],
  },
  {
    id: 'brand-api-docs',
    icon: Code,
    title: 'Brand API docs',
    description: 'The Brand API reference',
    route: ROUTES.brandApiDocs,
    productKeys: ['brand_api'],
  },
  {
    id: 'developer-mcp',
    icon: Code,
    title: 'MCP Protocol',
    description: 'Model Context Protocol servers',
    route: ROUTES.developerMcp,
    productKeys: ['brand_api'],
  },
  {
    id: 'mcp-protocol-detail',
    icon: Code,
    title: 'MCP server',
    description: "One protocol's settings",
    route: ROUTES.mcpProtocolDetail,
    productKeys: ['brand_api'],
  },
  {
    id: 'developer-integrations',
    icon: Code,
    title: 'Integrations',
    description: 'Connect other tools',
    route: ROUTES.developerIntegrations,
    productKeys: ['brand_api'],
  },
  {
    /*
     * No productKeys: Team is not sold and never will be. It is the screen an administrator
     * uses to give colleagues access to the products the company already bought -- which is
     * why it cannot itself be one.
     *
     * The two roles that may manage people, from `role_seed.sql`. A team member manages
     * nobody. `manager` was the third until that role went on 30 September 2026.
     */
    id: 'team',
    icon: Users,
    title: 'Team',
    description: 'People and their access',
    route: ROUTES.team,
    roleKeys: ['primary-admin', 'admin'],
  },
  {
    /*
     * NOT a sidebar row since 8 October 2026 -- it moved into the profile menu at the foot of
     * the rail, which is where the redesign puts account access. `/account` is unchanged and
     * still routes; only the way in moved.
     *
     * It is also what "Settings & Help" opens, since its own description is
     * "Profile & settings" and no separate settings screen exists to invent.
     */
    id: 'account',
    icon: Settings,
    title: 'My Account',
    description: 'Profile & settings',
    route: ROUTES.account,
  },
  {
    /*
     * Review Management: the question builder. The platform operator's, like the receiver pool.
     * `REVIEW_MANAGEMENT_ROLES` is the one place that says who may open it; an administrator role
     * for the platform is added to that list when it exists. The endpoints behind it are
     * `@SuperAdminOnly` and need the same change on the server.
     */
    id: 'review-management',
    icon: Star,
    title: 'Review Management',
    description: 'Manage reviews & ratings',
    route: ROUTES.reviewQuestions,
    roleKeys: REVIEW_MANAGEMENT_ROLES,
  },
  {
    /*
     * Support & Docs: the operator's own management of documentation, FAQs and tickets. Like Review
     * Management it belongs to no customer, so the row is restricted by role and what refuses anybody
     * else is every endpoint behind it.
     */
    id: 'support-management',
    icon: HelpCircle,
    title: 'Support & Docs',
    description: 'Manage support & documentation',
    route: ROUTES.supportAdmin,
    roleKeys: ['super-admin'],
  },
  {
    /*
     * The warmup receiver pool: the platform operator's. The receivers belong to no
     * customer, so there is no product to hold and the row is restricted by role.
     * Hidden from everybody else; what refuses them is every endpoint behind it.
     */
    id: 'warmup-receivers',
    icon: Mail,
    title: 'Email Warmup',
    description: 'Manage email warmup settings',
    route: ROUTES.warmupReceivers,
    roleKeys: ['super-admin'],
  },
  {
    /*
     * Billing and plans: the platform operator's, like the receiver pool. Three rows, one parent that
     * is an overview and two children that are the work: the plan catalogue and the queue of requests
     * for a custom plan. Hidden from everybody else; what refuses them is every endpoint behind it.
     */
    id: 'platform-billing',
    icon: CreditCard,
    title: 'Billing & Plans',
    description: 'Plans, prices and plan requests',
    route: ROUTES.platformBilling,
    roleKeys: ['super-admin'],
  },
  {
    id: 'platform-billing-plans',
    icon: Layers,
    title: 'Plans',
    description: 'The plan catalogue',
    route: ROUTES.platformBillingPlans,
    roleKeys: ['super-admin'],
  },
  {
    id: 'platform-billing-requests',
    icon: ClipboardCheck,
    title: 'Plan requests',
    description: 'Custom plan requests to approve or reject',
    route: ROUTES.platformBillingRequests,
    roleKeys: ['super-admin'],
  },
] as const;

/**
 * One screen by id, for the menu to build a row from.
 *
 * <p>Throws rather than returning undefined. A menu row naming a screen that does not exist
 * is a programming error that would otherwise surface as a row linking to `undefined`, and
 * this runs at module load — so it fails on the first render rather than on the first click.
 */
export function screenById(id: string): Screen {
  const screen = SCREENS.find((candidate) => candidate.id === id);
  if (!screen) {
    throw new Error(`No screen registered with id "${id}"`);
  }
  return screen;
}
